import React from 'react';
import { Container, Row, Col, Card, Button, Modal, Table, Badge } from "react-bootstrap";
import Papa from 'papaparse';
import SignupHero from './SignupHero';
import VideoIntro from './VideoIntro';
import VideoIntroLanding from './VideoIntroLanding';
import MarketingLander from './MarketingLander';
import FromThisToThis from './FromThisToThis.js';
import WorkshopAgenda from './WorkshopAgenda';
import LearningOutcomes from './LearningOutcomes';
import LocationDetails from './LocationDetails';
import CurriculumSection from './CurriculumSection';
import Markdown from './Markdown.js'; 
import JsonService from './JsonService';
import SocialsBar from './SocialsBar';
import VideoMenuBinder from './VideoMenuBinder.js';



// in React code
import { BaseData, Any, List, ModernFloatRangeExampleClass } from './BaseData.js';

// Style
const spine = {
  position: 'absolute',
  left: '0.5rem',
  top: '0.5rem',
  bottom: '0.5rem',
  width: '2px',
  backgroundColor: '#0d6efd',
};


class TimelineAgendaData extends BaseData {
  static annotations = {
    title: String,
    description: String,
  };
}

class RenderControlData extends BaseData {
  static annotations = {
    render_control_type: String, // e.g., 'hero', 'signup_hero', 'workshop', 'learning_outcomes'
  };
}

// ADD: a small data class (place with the other *Data classes)
class SocialsBarData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    label: String,
    items: List(Any),
  });
}

class HeroSectionData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    useImageUnderlay: Boolean,

  });
}

class MarkdownData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    content: String,
  });
}

class WorkshopSectionData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    preHeading: String,
    heading: String,
    description: String,
    cardTitle: String,
    timelineData: List(TimelineAgendaData),
  });
}


// --- ADD after TimelineAgendaData (NEW) ---
class BaseCardData extends BaseData {
  static annotations = {
    render_control_type: String, // 'event_card' | 'registration_form'
  };
}

class EventCardData extends BaseCardData {
  static annotations = Object.assign({}, BaseCardData.annotations, {
    title: String,
    description: String,
    date: String,
    location: String,
    badge: String,
    image: String,
    actionLabel: String,
    actionHref: String,
  });
}

class VideoMenuButtonData extends BaseData {
  static annotations = {
    label: String,
    targetScene: String,
    variant: String, // e.g., 'primary', 'outline-secondary'
  };
}

class VideoMenuBinderData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    initial_scene: String,
    loops: Any,        // { scene: url, ... }
    transitions: Any,  // { 'A->B': url, ... }
    buttons: List(VideoMenuButtonData),
  });
}




class FormFieldData extends BaseData {
  static annotations = {
    name: String,
    label: String,
    type: String,         // 'text' | 'email' | ...
    placeholder: String,
    required: Boolean,
  };
}

class RegistrationFormCardData extends BaseCardData {
  static annotations = Object.assign({}, BaseCardData.annotations, {
    formTitle: String,
    formSubtitle: String,
    actionUrl: String,
    submitLabel: String,
    fields: List(FormFieldData),
  });
}

// --- REPLACE the existing SignupHeroData with this (CHANGED) ---
class SignupHeroData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    beTheFooter: Boolean,

    // non-card content (a)
    title: String,
    subtitle: String,
    ctaLabel: String,
    ctaHref: String,

    // cards list (b) — raw, will be instanced via mkCardInstance
    cards: List(Any),
  });
}


// --- Known JSON URLs in /public ---
const GLOBAL_CONTENT_URL = '/landers/artlab.json';


// --- ADD factory helpers (NEW) ---
function mkCardInstance(c) {
  if (!c || !c.render_control_type) return new BaseCardData(c || {});
  if (c.render_control_type === 'event_card') return new EventCardData(c);
  if (c.render_control_type === 'registration_form') return new RegistrationFormCardData(c);
  return new BaseCardData(c);
}

// --- UPDATE mkSectionInstance to pre-instantiate cards for signup_hero (CHANGED snippet) ---
function mkSectionInstance(s) {
  if (s.render_control_type === 'hero') return new HeroSectionData(s);
  if (s.render_control_type === 'hero_landing') return new HeroSectionData(s);
  if (s.render_control_type === 'socials_bar') return new SocialsBarData(s);

  if (s.render_control_type === 'signup_hero') {
    const inst = new SignupHeroData(s);
    inst.cards = (inst.cards || []).map(mkCardInstance); // NEW
    return inst;
  }

  if (s.render_control_type === 'workshop') return new WorkshopSectionData(s);
  if (s.render_control_type === 'markdown') return new MarkdownData(s);

  if (s.render_control_type === 'video_menu_binder') return new VideoMenuBinderData(s);

  return new RenderControlData(s);
}


export default function ArtLab({ content_url, children }) {
  const [content, setContent] = React.useState(null);
  const [sections, setSections] = React.useState([]);
  const [error, setError] = React.useState(null);
  // const GLOBAL_CONTENT_URL = '/landers/artlab.json';

  React.useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        // 1) load content.json
        const global = await JsonService.fetchJson({ url: content_url, signal: ctrl.signal, cache: 'no-store' });
        setContent(global);

        // 2) load each section json listed in content.sections
        const urls = Array.isArray(global.sections) ? global.sections : [];
        const rawSections = await Promise.all(
          urls.map((u) => JsonService.fetchJson({ url: u, signal: ctrl.signal, cache: 'no-store' }))
        );

        // 3) parse into typed section instances
        setSections(rawSections.map(mkSectionInstance));
      } catch (e) {
        if (e.name !== 'AbortError') setError(e);
      }
    })();
    return () => ctrl.abort();
  }, []);

  if (error) return <div>Error: {String(error)}</div>;
  if (!content || sections.length === 0) return <div>Loading…</div>;

  return (
    <div>
      {sections.map((section, i) => {
        if (section.render_control_type === 'socials_bar') {
          return (
            <SocialsBar
              key={i}
              items={section.items}
              label={section.label}
            />
          );
        }

        if (section.render_control_type === 'video_menu_binder') {
          return (
            <VideoMenuBinder
              key={i}
              initial_scene={section.initial_scene}
              loops={section.loops}
              transitions={section.transitions}
              buttons={section.buttons}
              style={{ width: 480, maxWidth: '92vw' }} 
            />
          );
        }


        if (section.render_control_type === 'hero') {
          return <VideoIntro key={i} 
          useImageUnderlay={section.useImageUnderlay }
          yt_url = {section.yt_url}
          img_path = {section.img_path}
          hero_button_label = {section.hero_button_label}
          hero_button_link = {section.hero_button_link}
          hero_area_text = {section.hero_area_text}
          call_to_action_title = {section.call_to_action_title}   
          call_to_action_link = {section.call_to_action_link}   
          iframeStyle = {section.video_frame_style}   
          />;
        }
        if (section.render_control_type === 'from_this_to_this') {
          return (
            <FromThisToThis
              key={i}
              title={section.title}
              subtitle={section.subtitle}
              leftImageUrl={section.leftImageUrl}
              centerText={section.centerText}
              rightImageUrl={section.rightImageUrl}
            />
          );
        }

        if (section.render_control_type === 'hero_landing') {
          //return <VideoIntroLanding key={i} 
          // MarketingLander
          return <MarketingLander key={i} 
          useImageUnderlay={section.useImageUnderlay }
          yt_url = {section.yt_url}
          img_path = {section.img_path}
          hero_button_label = {section.hero_button_label}
          hero_button_link = {section.hero_button_link}
          hero_area_text = {section.hero_area_text}
          call_to_action_title = {section.call_to_action_title}   
          call_to_action_link = {section.call_to_action_link}   
          iframeStyle = {section.video_frame_style}   
          />;
        }
        

        if (section.render_control_type === 'signup_hero') {
          return <SignupHero key={i} 
            beTheFooter = {section.beTheFooter}
              title= {section.title}
              subtitle= {section.subtitle}
              ctaLabel= {section.ctaLabel}
              ctaHref= {section.ctaHref}
              cards = {section.cards}
          />;
        }
        if (section.render_control_type === 'markdown') {
          return <section >
              <Container>
                    <Markdown key={i} md={section.content} />;
              </Container>
            </section>
        }        
        if (section.render_control_type === 'workshop') {
          return (
            <WorkshopAgenda
              key={i}
              preHeading={section.preHeading}
              heading={section.heading}
              description={section.description}
              cardTitle={section.cardTitle}
              timelineData={section.timelineData}
              spineStyle={spine}
            />
          );
        }
        if (section.render_control_type === 'learning_outcomes') {
          return <LearningOutcomes key={i} outcomes={content.outcomes} />;
        }
        return null;
      })}
      {children}
    </div>
  );
}